import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ProfileContext } from "../context/ProfileContext";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="ph-tooltip">
        <p className="ph-tooltip-label">{label}</p>
        <p className="ph-tooltip-value">${payload[0].value.toLocaleString()}</p>
      </div>
    );
  }
  return null;
};

const InfoRow = ({ label, value, valueClass = "" }) => (
  <div className="pdm-info-row">
    <span className="pdm-info-label">{label}</span>
    <span className={`pdm-info-value ${valueClass}`}>{value}</span>
  </div>
);

function PaymentDetailModal({ open, onClose, row, filter, fetchTransactions }) {
  const [chartFilter, setChartFilter] = useState(filter);
  const { modalChartData } = useContext(ProfileContext);

  useEffect(() => { setChartFilter(filter) }, [filter]);
  useEffect(() => {
    if (open && row) { fetchTransactions(0, 8, chartFilter, row._id) }
  }, [chartFilter, open, row]);

  if (!row) return null;
  const subtitleMap = {
    Weekly: "Last 7 days",
    Monthly: "Last 12 months",
    Yearly: "All years",
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") return
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth disableEscapeKeyDown PaperProps={{ className: "pdm-dialog-paper" }}>
      <DialogTitle className="pdm-dialog-title">
        <div className="pdm-title-row">
          <div className="pdm-title-left">
            <div className="pdm-avatar">{row.patient.charAt(0).toUpperCase()}</div>
            <div>
              <h5 className="pdm-patient-name">{row.patient}</h5>
              <span className="pdm-patient-email">{row.email}</span>
            </div>
          </div>
          <IconButton onClick={onClose} className="pdm-close-btn" size="small"><Close /></IconButton>
        </div>
      </DialogTitle>

      <DialogContent className="pdm-dialog-content">
        <div className="pdm-details-card">
          <h6 className="pdm-section-title">Transaction Details</h6>
          <div className="pdm-details-grid">
            <InfoRow label="Date" value={new Date(row.lastTransaction).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} />
            <InfoRow label="Amount" value={`$${row.amount.toFixed(2)}`} valueClass="pdm-amount-value" />
            <InfoRow label="Total Transactions made" value={row.transactionCount} />
          </div>
        </div>

        <div className="pdm-chart-card">
          <div className="pdm-chart-header">
            <div>
              <h6 className="pdm-chart-title">Payment Activity</h6>
              <p className="pdm-chart-subtitle">{subtitleMap[chartFilter]}</p>
            </div>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel
                id="modal-period-label"
                sx={{ fontSize: "1.2rem" }}
              >
                Period
              </InputLabel>
              <Select
                labelId="modal-period-label"
                id="modal-period-select"
                value={chartFilter}
                label="Period"
                onChange={(e) => setChartFilter(e.target.value)}
                sx={{
                  fontSize: "1.2rem",
                  "& .MuiSelect-select": { fontSize: "1.2rem" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { "& .MuiMenuItem-root": { fontSize: "1.2rem" } },
                  },
                }}
              >
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Monthly">Monthly</MenuItem>
                <MenuItem value="Yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={modalChartData} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="modalAreaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1A76EA" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#1A76EA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#888" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#888" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="amount"
                stroke="#1A76EA"
                strokeWidth={2.5}
                fill="url(#modalAreaGrad)"
                dot={{ r: 3, fill: "#1A76EA", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

      </DialogContent>
    </Dialog>
  );
}

export default PaymentDetailModal;
